// Where the game server lives, and every URL the client builds for it (architecture §12): the
// client may be served from another origin than the server (e.g. a static host for the page and
// a VPS for the game server), so all server URLs go through this module:
// - /api/* discovery calls (net/discovery.ts);
// - the WebSocket URL from /api/find (net/socket.ts): http → ws, https → wss;
// - /assets/manifest.json (assets/manifest.ts), /assets/<file> and /skins/* (assets/loader.ts).
//
// The server base is chosen once at boot (main.ts → App):
//   1. `?server=<url>` in the page URL: a runtime override for testing another server, honoured
//      ONLY in a dev build (`allowOverride`, main.ts passes `import.meta.env.DEV`). A production
//      page signs players in and sends their Privy tokens (Bearer, HELLO, identity token) to the
//      server base, so a link like `https://ballio.pages.dev/?server=https://evil.example` must
//      not be able to redirect them (security audit, client lane);
//   2. the build-time `VITE_SERVER_URL`;
//   3. `""` = the page's own origin (dev: the Vite proxy forwards /api, /ws, /assets, /skins).
// A base is an absolute http(s) URL without credentials, query or hash, stored without a trailing
// slash. A path in it (`https://host/ballio`) prefixes every path the client builds; a `wsUrl` the
// server sends is resolved with normal URL rules against the base, so a root-relative one
// (`/ws?room=ffa-1`, or `/g/2/ws?…` behind a multi-process proxy) keeps the server's own root.
//
// Implementer: client net/game. Consumers: main.ts, app.ts, net/discovery.ts, net/socket.ts,
// assets/manifest.ts, assets/loader.ts.

/** The page query parameter that overrides the server base at runtime. */
export const SERVER_PARAM = 'server';

/**
 * Normalises a configured server URL: `""` for empty/blank/undefined (same origin), the URL's
 * origin + path without a trailing slash for an http(s) URL, or null when it is not usable
 * (unparsable, another scheme, credentials, a query or a hash).
 */
export function parseServerBase(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim();
  if (s === '') return '';
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  if (u.username !== '' || u.password !== '') return null;
  if (u.search !== '' || u.hash !== '') return null;
  return (u.origin + u.pathname).replace(/\/+$/, '');
}

export interface ServerBaseSources {
  /** The build-time value (`import.meta.env.VITE_SERVER_URL`). */
  readonly envUrl?: string | undefined;
  /** The page query string (`location.search`), for the `?server=` override. */
  readonly search?: string;
  /** Warning sink for an unusable value (default console.warn). */
  readonly warn?: (msg: string) => void;
  /**
   * Whether `?server=` may override the build's server (default false). Only dev builds pass
   * true: in production the server base receives the player's sign-in tokens.
   */
  readonly allowOverride?: boolean;
}

/**
 * The server base for this page: with `allowOverride`, a valid `?server=` wins (an empty
 * `?server=` forces the page's own origin); then VITE_SERVER_URL, then `""`. Unusable values,
 * and any `?server=` without `allowOverride`, are warned about and skipped.
 */
export function chooseServerBase(src: ServerBaseSources = {}): string {
  const warn = src.warn ?? ((msg: string) => console.warn(msg));
  const param = new URLSearchParams(src.search ?? '').get(SERVER_PARAM);
  if (param !== null && src.allowOverride !== true) {
    warn(`ball.io: ignoring ?${SERVER_PARAM}= (only dev builds may pick another server)`);
  } else if (param !== null) {
    const fromParam = parseServerBase(param);
    if (fromParam !== null) return fromParam;
    warn(`ball.io: ignoring ?${SERVER_PARAM}=${param} (only http and https URLs are allowed)`);
  }
  const fromEnv = parseServerBase(src.envUrl);
  if (fromEnv !== null) return fromEnv;
  warn(`ball.io: ignoring VITE_SERVER_URL "${src.envUrl ?? ''}" (not an http(s) URL)`);
  return '';
}

/**
 * A client-built server URL: `base` + `path` (`path` starts with "/", query allowed). With the
 * `""` base the result is root-relative, i.e. on the page's own origin.
 */
export function serverUrl(base: string, path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return base.replace(/\/+$/, '') + p;
}

/**
 * The WebSocket URL for a discovery `wsUrl` (relative like `/ws?room=ffa-1`, or absolute):
 * relative URLs resolve against the server base (the page when the base is `""`), then
 * http → ws and https → wss. ws:/wss: URLs pass through.
 */
export function serverWsUrl(wsUrl: string, base: string, pageHref: string): string {
  const trimmed = base.replace(/\/+$/, '');
  const u = new URL(wsUrl, trimmed === '' ? pageHref : `${trimmed}/`);
  if (u.protocol === 'http:') u.protocol = 'ws:';
  else if (u.protocol === 'https:') u.protocol = 'wss:';
  return u.toString();
}
